// src/utils/imageHandler.js
// Comprehensive image handling with authentication, caching, and error recovery

import { API_BASE, ensureCsrf } from '../lib/http';

// Image cache to prevent unnecessary reloads
const imageCache = new Map();
const failedImages = new Set();

// Token refresh interval to keep authentication fresh
let tokenRefreshInterval = null;
const TOKEN_REFRESH_INTERVAL = 3 * 60 * 1000; // 3 minutes

// Avatar refresh interval to keep avatar URLs fresh
let avatarRefreshInterval = null;
const AVATAR_REFRESH_INTERVAL = 2 * 60 * 1000; // 2 minutes

// Start periodic token refresh
const startTokenRefresh = () => {
  if (tokenRefreshInterval) return;
  
  tokenRefreshInterval = setInterval(async () => {
    try {
      console.log('Performing periodic token refresh...');
      await ensureCsrf();
      
      // Also try to refresh session
      try {
        await fetch(`${API_BASE}/api/profile`, {
          method: 'GET',
          credentials: 'include',
          headers: { 'Accept': 'application/json' }
        });
      } catch (error) {
        console.log('Periodic session refresh failed:', error);
      }
    } catch (error) {
      console.log('Periodic token refresh failed:', error);
    }
  }, TOKEN_REFRESH_INTERVAL);
};

// Start periodic avatar refresh
const startAvatarRefresh = () => {
  if (avatarRefreshInterval) return;
  
  avatarRefreshInterval = setInterval(() => {
    console.log('Performing periodic avatar refresh...');
    
    // Find all avatar images and refresh them
    const avatarImages = document.querySelectorAll('img[src*="avatar"], img[alt*="avatar"], img[alt*="Avatar"]');
    avatarImages.forEach(img => {
      if (img.src && !img.src.includes('placeholder') && !img.src.includes('blob:')) {
        // Add cache busting parameter
        const separator = img.src.includes('?') ? '&' : '?';
        const newSrc = `${img.src}${separator}refresh=${Date.now()}`;
        
        // Create a new image to test if the URL is still valid
        const testImg = new Image();
        testImg.crossOrigin = 'use-credentials';
        testImg.onload = () => {
          // If the new URL works, update the original image
          img.src = newSrc;
          console.log('Avatar refreshed successfully');
        };
        testImg.onerror = () => {
          console.log('Avatar refresh failed, keeping current URL');
        };
        testImg.src = newSrc;
      }
    });
  }, AVATAR_REFRESH_INTERVAL);
};

// Refresh user avatars to prevent 5-minute expiry issues
const refreshUserAvatars = () => {
  console.log('Refreshing user avatars to prevent expiry issues...');
  
  // Find all user avatar images on the page
  const userAvatarImages = document.querySelectorAll('img[src*="/uploads/avatars/"]');
  
  userAvatarImages.forEach(img => {
    const currentSrc = img.src;
    if (currentSrc && !currentSrc.includes('placeholder')) {
      // Add cache-busting parameter to refresh the image
      const separator = currentSrc.includes('?') ? '&' : '?';
      const refreshedSrc = `${currentSrc.split('?')[0]}${separator}t=${Date.now()}`;
      
      // Create a new image to test if the refreshed URL works
      const testImg = new Image();
      testImg.onload = () => {
        console.log('User avatar refreshed successfully:', refreshedSrc);
        img.src = refreshedSrc;
      };
      testImg.onerror = () => {
        console.log('User avatar refresh failed, keeping current src:', currentSrc);
      };
      testImg.src = refreshedSrc;
    }
  });
};

// Start refresh intervals when module loads
if (typeof window !== 'undefined') {
  startTokenRefresh();
  startAvatarRefresh();
  
  // Start user avatar refresh every 4 minutes to prevent 5-minute expiry issues
  setInterval(() => {
    refreshUserAvatars();
  }, 4 * 60 * 1000); // 4 minutes
}

// Default placeholder images
const PLACEHOLDERS = {
  user: '/images/user-placeholder.png',
  shop: '/images/shop-placeholder.png',
  service: '/images/service-placeholder.png',
  general: '/images/placeholder.png'
};

/**
 * Normalize image URL with proper authentication and error handling
 */
export const normalizeImageUrl = (url, type = 'general') => {
  if (!url || url === 'null' || url === 'undefined') return PLACEHOLDERS[type] || PLACEHOLDERS.general;
  
  // Already absolute URL
  if (/^https?:\/\//i.test(url)) return url;
  
  // Data URL or blob
  if (url.startsWith('data:') || url.startsWith('blob:')) return url;
  
  // Clean and construct URL
  const cleanUrl = String(url).replace(/^\.?\/*/, '');
  
  // For user avatars, use direct static file serving (no auth required)
  if (type === 'user') {
    let avatarUrl = '';
    
    // Handle both full paths and relative paths for avatars
    if (cleanUrl.startsWith('uploads/avatars/')) {
      avatarUrl = `${window.location.origin}/${cleanUrl}`;
    } else if (cleanUrl.startsWith('/uploads/avatars/')) {
      avatarUrl = `${window.location.origin}${cleanUrl}`;
    } else if (cleanUrl.includes('avatars/')) {
      // Handle cases where the path might be just the filename
      avatarUrl = `${window.location.origin}/uploads/avatars/${cleanUrl}`;
    } else {
      // Fallback to placeholder
      return PLACEHOLDERS[type] || PLACEHOLDERS.general;
    }
    
    // Return the avatar URL without cache-busting initially
    // Cache-busting will be added in error handling if needed
    return avatarUrl;
  }
  
  // For other types, use API_BASE with potential auth
  return `${API_BASE}/${cleanUrl}`;
};

/**
 * Enhanced image component with error handling and retry logic
 */
export const createImageHandler = (type = 'general') => {
  // Define handleError function that can be called recursively
  const handleError = async (event, originalUrl, retryCount = 0) => {
      const img = event.target;
      const maxRetries = type === 'user' ? 1 : 3; // Fewer retries for user avatars
      
      console.log(`Image error for ${type}:`, { originalUrl, retryCount, eventType: event.type });
      
      // For user avatars, try cache-busting before showing placeholder
      if (type === 'user') {
        console.log(`User avatar failed, trying cache-busting for ${originalUrl}`);
        
        // Try with cache-busting parameter
        const separator = originalUrl.includes('?') ? '&' : '?';
        const cacheBustedUrl = `${originalUrl}${separator}t=${Date.now()}`;
        
        // Create a new image to test the cache-busted URL
        const testImg = new Image();
        testImg.onload = () => {
          console.log(`Cache-busted avatar loaded successfully: ${cacheBustedUrl}`);
          img.src = cacheBustedUrl;
        };
        testImg.onerror = () => {
          console.log(`Cache-busted avatar also failed, showing placeholder for ${originalUrl}`);
          img.src = PLACEHOLDERS[type] || PLACEHOLDERS.general;
          img.onerror = null; // Remove error handler to prevent loops
        };
        testImg.src = cacheBustedUrl;
        return;
      }
      
      // Prevent infinite loops
      if (retryCount >= maxRetries) {
        console.log(`Max retries reached for ${originalUrl}, showing placeholder`);
        img.src = PLACEHOLDERS[type] || PLACEHOLDERS.general;
        img.onerror = null; // Remove error handler to prevent loops
        return;
      }
      
      // Only retry if it's a network error, not a touch event
      if (event.type === 'error' && originalUrl && !failedImages.has(originalUrl)) {
        // Mark as failed temporarily
        failedImages.add(originalUrl);
        
        try {
          console.log(`Attempting to refresh authentication for ${originalUrl}`);
          
          // Strategy 1: Try to get a fresh image URL from the server
          let freshImageUrl = null;
          try {
            // Make a request to get fresh image data
            const profileResponse = await fetch(`${API_BASE}/api/profile`, {
              method: 'GET',
              credentials: 'include',
              headers: {
                'Accept': 'application/json',
              }
            });
            
            if (profileResponse.ok) {
              const profileData = await profileResponse.json();
              // Get the fresh avatar URL from the server response
              freshImageUrl = profileData.avatarUrl;
              console.log('Got fresh image URL from server:', freshImageUrl);
            }
          } catch (error) {
            console.log('Failed to get fresh image URL:', error);
          }
          
          // Strategy 2: Refresh CSRF token
          try {
            await ensureCsrf();
            console.log('CSRF token refreshed');
          } catch (error) {
            console.log('CSRF refresh failed:', error);
          }
          
          // Retry with fresh authentication
          setTimeout(() => {
            if (img.src !== PLACEHOLDERS[type]) {
              let retryUrl;
              
              // Use fresh URL from server if available, otherwise use original with cache busting
              if (freshImageUrl) {
                retryUrl = normalizeImageUrl(freshImageUrl, type);
              } else {
                const newUrl = normalizeImageUrl(originalUrl, type);
                const separator = newUrl.includes('?') ? '&' : '?';
                retryUrl = `${newUrl}${separator}t=${Date.now()}&retry=${retryCount + 1}`;
              }
              
              console.log(`Retrying with URL: ${retryUrl}`);
              
              // Create a new image element to avoid caching issues
              const newImg = new Image();
              newImg.crossOrigin = 'use-credentials'; // Ensure credentials are sent
              newImg.onload = () => {
                console.log('Image loaded successfully on retry');
                img.src = retryUrl;
                failedImages.delete(originalUrl); // Remove from failed cache
              };
              newImg.onerror = () => {
                console.log('Image retry failed, trying next attempt');
                // If this retry also fails, try the next retry
                if (retryCount + 1 < maxRetries) {
                  // Recursively call handleError with increased retry count
                  setTimeout(() => {
                    const newEvent = { ...event, target: img };
                    handleError(newEvent, originalUrl, retryCount + 1);
                  }, 1000 + (retryCount * 500)); // Increasing delay
                } else {
                  console.log('All retry attempts failed, showing placeholder');
                  img.src = PLACEHOLDERS[type] || PLACEHOLDERS.general;
                  img.onerror = null;
                }
              };
              newImg.src = retryUrl;
            }
          }, 500 + (retryCount * 500)); // Increasing delay for each retry
          
        } catch (error) {
          console.log(`Authentication refresh failed for ${originalUrl}:`, error);
          // If all refresh strategies fail, show placeholder
          img.src = PLACEHOLDERS[type] || PLACEHOLDERS.general;
          img.onerror = null;
        }
      } else {
        console.log(`Not retrying ${originalUrl} (touch event or already failed)`);
        // For touch events or already failed images, just show placeholder
        img.src = PLACEHOLDERS[type] || PLACEHOLDERS.general;
        img.onerror = null;
      }
    };

  return {
    /**
     * Get normalized image URL
     */
    getUrl: (url) => normalizeImageUrl(url, type),
    
    /**
     * Handle image load error with retry logic
     */
    handleError,
    
    /**
     * Handle successful image load
     */
    handleLoad: (event, originalUrl) => {
      // Remove from failed cache on successful load
      failedImages.delete(originalUrl);
      
      // Cache the successful URL
      if (originalUrl) {
        imageCache.set(originalUrl, event.target.src);
      }
    },
    
    /**
     * Get cached URL if available
     */
    getCachedUrl: (url) => {
      return imageCache.get(url) || normalizeImageUrl(url, type);
    },
    
    /**
     * Clear cache for a specific URL
     */
    clearCache: (url) => {
      imageCache.delete(url);
      failedImages.delete(url);
    },
    
    /**
     * Clear all cache
     */
    clearAllCache: () => {
      imageCache.clear();
      failedImages.clear();
    }
  };
};

/**
 * Preload images with error handling
 */
export const preloadImages = async (urls, type = 'general') => {
  const handler = createImageHandler(type);
  const promises = urls.map(url => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        handler.handleLoad({ target: img }, url);
        resolve({ url, success: true });
      };
      img.onerror = () => {
        handler.handleError({ target: img }, url);
        resolve({ url, success: false });
      };
      img.src = handler.getUrl(url);
    });
  });
  
  return Promise.all(promises);
};

/**
 * React hook for image handling
 */
export const useImageHandler = (type = 'general') => {
  const handler = createImageHandler(type);
  
  return {
    getUrl: handler.getUrl,
    handleError: handler.handleError,
    handleLoad: handler.handleLoad,
    getCachedUrl: handler.getCachedUrl,
    clearCache: handler.clearCache,
    clearAllCache: handler.clearAllCache
  };
};

/**
 * Image component props generator
 */
export const getImageProps = (url, type = 'general', options = {}) => {
  const handler = createImageHandler(type);
  const normalizedUrl = handler.getCachedUrl(url);
  
  return {
    src: normalizedUrl,
    onError: (e) => handler.handleError(e, url),
    onLoad: (e) => handler.handleLoad(e, url),
    loading: options.loading || 'lazy',
    decoding: options.decoding || 'async',
    ...options
  };
};

// Export default handler for general use
export default createImageHandler('general');
